import { Component, OnInit, Input, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ProjectAppointmentService } from '../Services/project-appointment.service';
import { formatDate } from '@angular/common';

interface Appointment {
  id: string | number;
  // allow multi-day appointments
  // ISO datetime-local strings (e.g. 2025-11-11T09:00) or legacy date-only (yyyy-mm-dd)
  startTime: string; // yyyy-mm-dd or yyyy-mm-ddTHH:mm
  endTime?: string; // yyyy-mm-dd or yyyy-mm-ddTHH:mm (inclusive). If absent, treated as startTime
  title: string;
  location?: string;
  description?: string;
  projectId?: number;
  createdAt: number;
}

@Component({
  selector: 'app-project-schedule',
  templateUrl: './project-schedule.component.html',
  styleUrls: ['./project-schedule.component.css']
})
export class ProjectScheduleComponent implements OnInit, OnChanges {
  // base key; actual storageKey appends project id when provided
  baseStorageKey = 'project-schedule-appointments-v1';

  @Input() project: any | undefined; // Project | any
  appointments: Appointment[] = [];

  // model for the add/edit form
  form: {
    id: string | number;
    startTime: string; // ISO datetime-local preferred
    endTime: string;
    time: string; // legacy time field (kept for backward compatibility)
    title: string;
    description: string;
  } = {
    id: '',
    startTime: this.formatDateTime(new Date()),
    endTime: this.formatDateTime(new Date()),
    time: '09:00',
    title: '',
    description: ''
  };

  // currently selected date for the day-list view (YYYY-MM-DD)
  selectedDate = this.formatDate(new Date());

  // visible month for month view (first day of month)
  visibleMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);

  // compute storage key scoped to project when available
  get storageKey() {
    const id = this.project && (this.project.id || this.project.projectId || this.project._id);
    return id ? `${this.baseStorageKey}-project-${id}` : this.baseStorageKey;
  }

  constructor(private http: HttpClient, private apptService: ProjectAppointmentService, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    //this.load();
    
        this.loadappointments();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // if project input changes, reload appointments from project-scoped storage
    if (changes['project']) {
    //   this.load();
        this.loadappointments();
    }
  }

  // helpers
  formatDate(d: Date): string {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }
  // produce a string suitable for <input type="datetime-local"> (no seconds)
  formatDateTime(d: Date): string {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
  }
 loadappointments() {
    try {
      const projId = this.project && (this.project.id || this.project.projectId || this.project._id);
      console.log('Loading appointments for project ID:', projId);
      
      if (projId) {
        const projIdNum = Number(projId);
        this.apptService.getByProjectId(projIdNum).subscribe({
          next: (appointments) => {
            console.log('Loaded appointments from server:', appointments);
            // Map ProjectAppointment to Appointment interface
            this.appointments = (appointments || []).map((appt: any) => {
              console.log('Mapping appointment:', appt);
              console.log('appt.startTime:', appt.startTime);
              console.log('appt.startDate:', appt.startDate);
              
              const mapped = {
                id: appt.id || Math.random().toString(36).substr(2, 9),
                startTime: appt.startTime || appt.startDate || '',
                endTime: appt.endTime || appt.endDate || appt.startTime || appt.startDate || '',
                title: appt.title || '(no title)',
                location: appt.location || '',
                description: appt.description || '',
                projectId: appt.projectId || projIdNum,
                createdAt: appt.createdAt || Date.now()
              } as Appointment;
              
              console.log('Mapped appointment:', mapped);
              return mapped;
            });
            console.log('Mapped appointments:', this.appointments);
            // Trigger change detection to ensure UI updates
            this.cdr.detectChanges();
          },
          error: (err) => {
            console.error('Failed to load appointments from server', err);
            // Fallback to project appointments if service call fails
            this.appointments = this.project?.appointments || [];
          }
        });
      } else {
        // No project ID, clear appointments
        console.log('No project ID, clearing appointments');
        this.appointments = [];
      }
    } catch (e) {
      console.error('Failed to load appointments', e);
      this.appointments = [];
    }
}

  load() {
    try {
      const raw = localStorage.getItem(this.storageKey);
      const arr = raw ? JSON.parse(raw) : [];
      // migrate older shape that used `date` -> map to startTime/endTime
      this.appointments = (arr || []).map((it: any) => {
        if (it.startTime) return it as Appointment;
        // legacy shape (date-only) -> convert to datetime-local by adding a default time
        const d = it.date || this.formatDate(new Date());
        const defaultTime = it.time || '09:00';
        const dt = `${d}T${defaultTime}`;
        return {
          id: it.id || Math.random().toString(36).substr(2, 9),
          startTime: dt,
          endTime: dt,
          title: it.title || '(no title)',
          description: it.description || '',
          createdAt: it.createdAt || Date.now()
        } as Appointment;
      });
    } catch (e) {
      console.error('Failed to load appointments', e);
      this.appointments = [];
    }
  }

  save() {
    // always persist locally
    localStorage.setItem(this.storageKey, JSON.stringify(this.appointments));

    // if a project is provided and has an id, persist to server using PutProject (assumes endpoint: /api/projects/{id})
    const projId = this.project && (this.project.id || this.project.projectId || this.project._id);
    if (projId) {
      // attach appointments into the project object and PUT
      try {
        const updatedProject = { ...this.project, appointments: this.appointments };
        const url = `/api/projects/${projId}`;
        this.http.put(url, updatedProject).subscribe({
          next: (resp) => {
            // update local project reference with server response when returned
            // (some APIs return the updated project)
            // eslint-disable-next-line no-console
            console.log('Project saved to server', resp);
            try {
              // If server returned the project, reflect it locally
              if (resp && typeof resp === 'object') {
                this.project = { ...this.project, ...(resp as any) };
              }
            } catch (e) { /* ignore */ }
          },
          error: (err) => {
            // eslint-disable-next-line no-console
            console.error('Failed to save project to server', err);
          }
        });
      } catch (e) {
        console.error('Error while sending project to server', e);
      }
    }
  }

  get filtered(): Appointment[] {
    // appointments that include the selectedDate
    return this.appointments
      .filter(a => this.dateInRange(this.selectedDate, a.startTime, a.endTime))
      .sort((x, y) => (x.startTime || '').localeCompare(y.startTime || ''));
  }

  startNewFor(date?: string) {
    const d = date || this.formatDate(new Date());
    // initialize datetime with a default time
    const defaultTime = '09:00';
    const dt = `${d}T${defaultTime}`;
    this.form = {
      id: '',
      startTime: dt,
      endTime: dt,
      time: defaultTime,
      title: '',
      description: ''
    };
    this.selectedDate = d;
  }

  addOrUpdate() {
    if (!this.form.title || !this.form.startTime) {
      alert('Please provide a title and a start time');
      return;
    }

    // ensure endTime
    if (!this.form.endTime) this.form.endTime = this.form.startTime;

    if (this.form.id) {
      // update - send to server first
      const projId = this.project && (this.project.id || this.project.projectId || this.project._id);
      const projIdNum = projId ? Number(projId) : undefined;
      const appointmentId = Number(this.form.id);
      const payload: any = {
        id: appointmentId,
        projectId: projIdNum,
        startTime: this.form.startTime,
        endTime: this.form.endTime || this.form.startTime,
        time: this.form.time || '',
        title: this.form.title,
        description: this.form.description
      };
      
      this.apptService.update(appointmentId, payload).subscribe({
        next: (updated) => {
          // reload appointments from server to reflect changes
          this.loadappointments();
          this.startNewFor(this.selectedDate);
        },
        error: (err) => {
          console.error('Failed to update appointment on server', err);
          alert('Failed to update appointment on server. See console for details.');
        }
      });
    } else {
      // create - send to server
      const projId = this.project && (this.project.id || this.project.projectId || this.project._id);
      const projIdNum = projId ? Number(projId) : undefined;
      const payload: any = {
        projectId: projIdNum,
        startTime: this.form.startTime,
        endTime: this.form.endTime || this.form.startTime,
        time: this.form.time || '',
        title: this.form.title,
        description: this.form.description
      };
      console.log(payload)
      this.apptService.create(payload).subscribe({
        next: (created) => {
          // reload appointments from server to reflect changes
          this.loadappointments();
          this.startNewFor(this.selectedDate);
        },
        error: (err) => {
          console.error('Failed to create appointment on server', err);
          alert('Failed to create appointment on server. See console for details.');
        }
      });
    }
  }

  edit(a: Appointment) {
    // populate form from appointment
    // ensure form gets a datetime-local string; accept legacy date-only
    const toDateTime = (s?: string) => {
      if (!s) return this.formatDateTime(new Date());
      return s.length === 10 ? `${s}T09:00` : s;
    };
    this.form = {
      id: a.id,
      startTime: toDateTime(a.startTime),
      endTime: toDateTime(a.endTime || a.startTime),
      time: '',
      title: a.title,
      description: a.description || ''
    };
    this.selectedDate = (a.startTime || '').slice(0, 10);
  }

  remove(id: string | number) {
    if (!confirm('Delete this appointment?')) return;
    
    const appointmentId = Number(id);
    console.log('Deleting appointment:', appointmentId);
    
    this.apptService.delete(appointmentId).subscribe({
      next: () => {
        console.log('Appointment deleted successfully, reloading...');
        // reload appointments from server to reflect changes
        this.loadappointments();
      },
      error: (err) => {
        console.error('Failed to delete appointment on server', err);
        alert('Failed to delete appointment on server. See console for details.');
      }
    });
  }

  prevDay() {
    const d = new Date(this.selectedDate + 'T00:00:00');
    d.setDate(d.getDate() - 1);
    this.selectedDate = this.formatDate(d);
  }

  nextDay() {
    const d = new Date(this.selectedDate + 'T00:00:00');
    d.setDate(d.getDate() + 1);
    this.selectedDate = this.formatDate(d);
  }

  importSample() {
    const today = this.formatDate(new Date());
    const tomorrow = this.formatDate(new Date(new Date().getTime() + 24 * 60 * 60 * 1000));
    // single-day
    this.appointments.push({
      id: 'sample1', startTime: today, endTime: today, time: '10:00', title: 'Site meeting', description: 'Meet with contractor', createdAt: Date.now()
    } as Appointment);
    // multi-day
    this.appointments.push({
      id: 'sample2', startTime: today, endTime: tomorrow, time: '', title: 'Installation (2 days)', description: 'Work on site', createdAt: Date.now()
    } as Appointment);
    this.save();
  }

  // -----------------
  // month view helpers
  // -----------------
  // returns true if dateStr is between start and end (inclusive). Strings are yyyy-mm-dd so lexicographic compare works
  dateInRange(dateStr: string, start?: string, end?: string) {
    // normalize to date-only strings (yyyy-mm-dd) for comparison
    const sRaw = start || dateStr;
    const eRaw = end || sRaw;
    const s = sRaw.slice(0, 10);
    const e = eRaw.slice(0, 10);
    return dateStr >= s && dateStr <= e;
  }

  // build a 6x7 grid (42 cells) for visibleMonth
  getMonthGrid() {
    const first = new Date(this.visibleMonth.getFullYear(), this.visibleMonth.getMonth(), 1);
    const startOffset = first.getDay(); // 0..6 (Sun..Sat)
    const cells: { date: Date; dateStr: string; inMonth: boolean; events: Appointment[] }[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(first);
      d.setDate(d.getDate() - startOffset + i);
      const ds = this.formatDate(d);
      const ev = this.appointments.filter(a => this.dateInRange(ds, a.startTime, a.endTime));
      cells.push({ date: d, dateStr: ds, inMonth: d.getMonth() === this.visibleMonth.getMonth(), events: ev });
    }
    return cells;
  }

  prevMonth() {
    this.visibleMonth = new Date(this.visibleMonth.getFullYear(), this.visibleMonth.getMonth() - 1, 1);
  }

  nextMonth() {
    this.visibleMonth = new Date(this.visibleMonth.getFullYear(), this.visibleMonth.getMonth() + 1, 1);
  }

  eventsForDate(dateStr: string) {
    return this.appointments.filter(a => this.dateInRange(dateStr, a.startTime, a.endTime));
  }

  selectDate(dateStr: string) {
    this.selectedDate = dateStr;
    this.startNewFor(dateStr);
  }

  getDisplayTime(appointment: Appointment): string {
    console.log('getDisplayTime called with startTime:', appointment.startTime);
    
    if (!appointment.startTime) {
      return 'All day';
    }
    
    // Try to parse the startTime as a Date regardless of format
    try {
      const date = new Date(appointment.startTime);
      console.log('Parsed date:', date, 'isValid:', !isNaN(date.getTime()));
      
      if (!isNaN(date.getTime())) {
        // Check if this is likely a date-only value (time is 00:00:00)
        const hours = date.getHours();
        const minutes = date.getMinutes();
        const seconds = date.getSeconds();
        
        console.log('Time components - hours:', hours, 'minutes:', minutes, 'seconds:', seconds);
        
        // If all time components are 0, it might be a date-only appointment
        if (hours === 0 && minutes === 0 && seconds === 0) {
          // But if the original string contains 'T', it was explicitly set to midnight
          if (appointment.startTime.includes('T')) {
            return date.toLocaleTimeString('en-US', { 
              hour: 'numeric', 
              minute: '2-digit',
              hour12: true 
            });
          } else {
            return 'All day';
          }
        } else {
          // Has actual time components
          return date.toLocaleTimeString('en-US', { 
            hour: 'numeric', 
            minute: '2-digit',
            hour12: true 
          });
        }
      }
    } catch (e) {
      console.error('Error parsing date:', appointment.startTime, e);
    }
    
    // Fall back to showing the raw startTime if we can't parse it
    return appointment.startTime;
  }
}
